import { ReactNode, useState } from 'react'
import { Text } from '@/components'
import BillUploadCard, { BillUploadCardProps } from './BillUploadCard'
import BillOnFileNotice from './BillOnFileNotice'
import UtilityAccountFields, {
  UtilityAccountFieldsProps,
} from './UtilityAccountFields'

export type UtilityAccountDetailsProps = {
  title: ReactNode
  subtitle?: ReactNode
  /** Props for the "Option 1: Upload document" card. */
  upload: BillUploadCardProps
  /** Props for the "Option 2: Enter manually" dynamic fields. */
  fields: UtilityAccountFieldsProps
  /** Call-to-action(s) rendered at the bottom (e.g. Next, or Skip + Next). */
  footer: ReactNode
  /**
   * When a bill is already on file, hides the upload card (and the Option 1/2
   * framing) and shows an "already uploaded" success banner, leaving the manual
   * fields editable.
   */
  alreadyUploaded?: boolean
}

/**
 * The shared "account details" page body: a heading, the bill-upload card
 * (Option 1), a manual-entry section with the dynamic account fields (Option 2),
 * and a footer slot for the page's call-to-action buttons.
 *
 * Used by both the multi-program enrollment account step and the
 * Profile → Utility Account page so they stay visually and behaviourally in sync.
 */
export default function UtilityAccountDetails({
  title,
  subtitle,
  upload,
  fields,
  footer,
  alreadyUploaded = false,
}: UtilityAccountDetailsProps) {
  // Local override so "Upload another bill" can re-reveal the uploader even
  // when a bill is already on file.
  const [showUploader, setShowUploader] = useState(false)
  const showOnFileNotice = alreadyUploaded && !showUploader

  return (
    <div className="h-full flex flex-col gap-4">
      <section className="flex flex-col gap-1">
        <Text variant="h3">{title}</Text>
        {subtitle}
      </section>

      <section className="flex flex-col gap-4">
        {showOnFileNotice ? (
          <BillOnFileNotice
            billUrl={upload.billUrl}
            onUploadAnother={() => setShowUploader(true)}
          />
        ) : (
          <>
            <BillUploadCard {...upload} />

            <div className="flex items-center gap-4">
              <div className="flex-1 border-solid border-t border-gray-300"></div>
              <Text variant="body2" className="text-themed-grey-500">
                OR
              </Text>
              <div className="flex-1 border-solid border-t border-gray-300"></div>
            </div>
          </>
        )}

        <div>
          {!showOnFileNotice && (
            <Text variant="body1" className="font-bold mb-4">
              Option 2: Enter manually
            </Text>
          )}
          <UtilityAccountFields {...fields} />
        </div>
      </section>

      <div className="mt-auto">{footer}</div>
    </div>
  )
}
